import flow from 'lodash/flow';
import {
  CorpInvoicingPeriod,
  CorporateFormFields,
} from 'src/components/enterprise/features/Corporate/components/Form/types/CorporateForm';
import {
  CorpUIResponse,
  CorpUIPayload,
  SSOIdentifierEnum,
} from 'src/types/Corp';
import {
  transformMarketsUIToModel,
  transformMarketsModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/MarketSelect/marketSelect.utils';
import { transformBrandModelToUI } from 'src/components/enterprise/features/Corporate/components/Form/BrandOptions/brandOptions.utils';
import {
  removePhoneNumberFormatting,
  getFormattedPhoneNumber,
} from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { transformAutomatedWelcomeEmails } from 'src/components/enterprise/features/Corporate/components/Form/AutomatedWelcomeEmails/AutomatedWelcomeEmails.utils';
import {
  transformTransactionFeeUIToModel,
  transformTransactionFeeModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/TransactionFeeField/TransactionFee.utils';
import {
  transformOrderingPolicyModelToUI,
  transformOrderingPolicyUiToModel,
} from 'src/components/enterprise/features/Corporate/components/Form/OrderingPolicy/utils/orderingPolicy.utils';
import {
  transformGhPlusSubscriptionUIToModel,
  transformGhPlusSubscriptionModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/GrubhubSeamlessPlusWrapper/grubhubSeamlessPlus.utils';
import {
  transformSFTPModelToUI,
  transformSFTPUIToModel,
  shouldEnableSFTP,
  cleanupFormSFTPFields,
} from 'src/components/enterprise/features/Corporate/components/Form/SFTP/utils/SFTP.utils';
import { transformSSOUIToModel } from 'src/components/enterprise/features/Corporate/components/Form/SSOWrapper/utils/SSO.utils';
import {
  transformInvoicePaymentMethodUIToModel,
  transformInvoicePaymentMethodModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/InvoicePaymentWrapper/InvoicePaymentMethod/utils/InvoicePaymentMethod.utils';
import {
  transformReportingModelToUI,
  transformReportingUItoModel,
} from 'src/components/enterprise/features/Corporate/components/Form/ReportingRequirementWrapper/ReportingRequirementControl/reportingRequirements.utils';
import {
  transformInvoicingMethodUItoModel,
  transformInvoicingMethodModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/InvoicingMethodWrapper/InvoicingMethodControl/invoicingMethod.utils';
import {
  transformUploadEmployeeUIToModel,
  transformUploadEmployeeModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/BulkUploadEmployeeWrapper/bulkUploadEmployeeWrapper.utils';
import {
  transformEffectiveDateModelToUI,
  transformEffectiveDateUIToModel,
} from 'src/components/enterprise/features/Corporate/components/Form/EffectiveDateControl/EffectiveDateControl.utils';
import { transformInvoiceContactAddress2ModelToUI } from 'src/components/enterprise/features/Corporate/components/Form/InvoiceContactAddress2Control/InvoiceContactAddress2Control.utils';
import { transformAutoInvoicingUIToModel } from 'src/components/enterprise/features/Corporate/components/Form/AutoInvoicingWrapper/AutoInvoicingControl/AutoInvoicingControl.utils';
import {
  transformPayloadToFormMerchantRestrictions,
  transformFormToPayloadMerchantRestrictions,
  transformFormToPayloadItemRestrictions,
  transformPayloadToFormItemRestrictions,
  cleanupFormRestrictionsFields,
} from 'src/common/OrderRestrictions/orderRestrictions.utils';
import { transformFirstInvoiceDateUIToModel } from 'src/components/enterprise/features/Corporate/components/Form/FirstInvoiceDate/utils/firstInvoiceDate.utils';
import { transformAdminUIProviderToModel } from 'src/components/AdminUIProvider/adminUiProvider.utils';
import {
  transformSalesPersonCodeModelToUI,
  transformSalesPersonCodeUIToModel,
} from 'src/components/enterprise/features/Corporate/components/Form/SalesPersonID/SalesPersonID.utils';
import {
  cleanupFormDnpFields,
  transformFormToPayloadDnp,
  transformPayloadToFormDnp,
} from 'src/components/enterprise/features/Corporate/components/Form/DinerNotificationPreferences/dinerNotificationPreferences.utils';
import {
  transformOrderCutoffUIToModel,
  transformOrderCutoffModelToUI,
} from 'src/components/enterprise/features/Corporate/components/Form/OrderCutoffControl/OrderCutoffControl.utils';
import { defaultCorpValues } from './constants/defaultCorpValues';

function transformFormToModel(values: CorporateFormFields): CorpUIPayload {
  const orderPolicyId = transformOrderingPolicyUiToModel(values).orderPolicyId;
  const sftpData = transformSFTPUIToModel({
    enableSFTP: values.enableSFTP,
    sftpNotificationEmailsString: values.sftpNotificationEmailsString,
    fileEncryptionEnabled: values.fileEncryptionEnabled,
    sftpOptions: values.sftpOptions,
  });

  const invoicePaymentMethod = transformInvoicePaymentMethodUIToModel({
    invoicePaymentMethod: values.invoicePaymentMethod,
    maxCreditCardCharge: values.maxCreditCardCharge,
  });

  const { defaultItemRestrictions, ...restValues } = values;

  const cleanupFormData = flow([
    cleanupFormDnpFields,
    cleanupFormSFTPFields,
    cleanupFormRestrictionsFields,
  ]);

  const uiCorpData = {
    ...cleanupFormData(restValues),
    orderCutOffTime: transformOrderCutoffUIToModel(
      values.disableMealCreditGracePeriod
    ),
    phoneNumber: removePhoneNumberFormatting(values.phoneNumber),
    markets: transformMarketsUIToModel(values.markets),
    automatedEmailsDisabled: transformAutomatedWelcomeEmails(
      values.automatedEmailsEnabled
    ),
    firmTransactionFee: transformTransactionFeeUIToModel(
      values.firmTransactionFee
    ),
    orderPolicyId,
    ...sftpData,
    ...invoicePaymentMethod,
    ...transformReportingUItoModel(values.reporting),
    ...transformInvoicingMethodUItoModel(
      values.invoicingMethod,
      values.reporting
    ),
    invoiceContactPhoneNumber: removePhoneNumberFormatting(
      values.invoiceContactPhoneNumber
    ),
    ...transformUploadEmployeeUIToModel(
      values.reporting,
      values.dinerLocationRequired,
      values.dinerDepartmentRequired
    ),
    effectiveDate: transformEffectiveDateUIToModel(values.effectiveDate),
    autoInvoicingEnabled: transformAutoInvoicingUIToModel(
      values.autoInvoicingEnabled
    ),
    defaultMerchantRestrictions: transformFormToPayloadMerchantRestrictions(
      values.defaultMerchantRestrictions
    ),
    dinerNotificationPreferences: transformFormToPayloadDnp(
      values.dinerNotificationPreferences
    ),
    defaultItemRestrictions: transformFormToPayloadItemRestrictions(
      defaultItemRestrictions
    ),
    firstInvoiceDate: transformFirstInvoiceDateUIToModel(),
    adminUiProvider: transformAdminUIProviderToModel(),
    salesPersonCode: transformSalesPersonCodeUIToModel(values.salesPersonCode),
    ...transformSSOUIToModel({
      ssoEnabled: values.ssoEnabled,
      ssoProviderId: values.ssoProviderId,
      ssoUniqueIdentifier: values.ssoUniqueIdentifier,
      ssoRedirectEnabled: values.ssoRedirectEnabled,
      ssoRedirectUrl: values.ssoRedirectUrl,
    }),
  };

  return uiCorpData;
}

export function createCorporateModel(
  values: CorporateFormFields
): CorpUIPayload {
  const ghPlusSubscription = transformGhPlusSubscriptionUIToModel(
    values.ghPlusEnabled,
    values.ghPlusSubscription
  );
  return {
    ...defaultCorpValues,
    ...transformFormToModel(values),
    ghPlusSubscription,
  };
}

export function updateCorporateModel(
  values: CorporateFormFields,
  existingData: CorpUIResponse
): CorpUIPayload {
  const baseData = transformFormToModel(values);

  const ghPlusSubscription = transformGhPlusSubscriptionUIToModel(
    values.ghPlusEnabled,
    values.ghPlusSubscription,
    existingData.ghPlusSubscription
  );

  const firstInvoiceDate = transformFirstInvoiceDateUIToModel(
    existingData.firstInvoiceDate
  );

  return {
    ...existingData,
    ...baseData,
    ghPlusSubscription,
    firstInvoiceDate,
  };
}

export function transformCorporateModelToUI(
  values: CorpUIResponse,
  cateringServiceFee: string
): CorporateFormFields {
  const reporting = transformReportingModelToUI({
    locationsRequired: values.locationsRequired,
    departmentsRequired: values.departmentsRequired,
  });
  return {
    ...values,
    invoiceContactAddress2: transformInvoiceContactAddress2ModelToUI(
      values.invoiceContactAddress2
    ),
    brand: transformBrandModelToUI(values.brand),
    phoneNumber: getFormattedPhoneNumber(values.phoneNumber),
    markets: transformMarketsModelToUI(values.markets),
    automatedEmailsEnabled: transformAutomatedWelcomeEmails(
      values.automatedEmailsDisabled
    ),
    firmTransactionFee: transformTransactionFeeModelToUI(
      values.firmTransactionFee
    ),
    ...transformOrderingPolicyModelToUI({
      orderPolicyId: values.orderPolicyId,
      id: values.id,
    }),
    ghPlusEnabled: values.ghPlusEnabled,
    ghPlusSubscription: transformGhPlusSubscriptionModelToUI(
      values.ghPlusEnabled,
      values.ghPlusSubscription
    ),
    ...transformSFTPModelToUI(
      values.sftpNotificationEmails,
      values.sftpEmployeesEnabled,
      values.sftpExpenseCodesEnabled
    ),
    enableSFTP: shouldEnableSFTP(values),
    universalAccountsEnabled: values.universalAccountsEnabled,
    ssoIdentifier: values.ssoIdentifier as SSOIdentifierEnum,
    reporting,
    invoicingMethod: transformInvoicingMethodModelToUI(
      values.invoicingMethod,
      values.invoicingSubtotalMethod
    ),
    maxCreditCardCharge: transformInvoicePaymentMethodModelToUI(
      values.maxCreditCardCharge
    ),
    invoiceContactPhoneNumber: getFormattedPhoneNumber(
      values.invoiceContactPhoneNumber
    ),
    salesPersonCode: transformSalesPersonCodeModelToUI(values.salesPersonCode),
    cateringServiceFee,
    ...transformUploadEmployeeModelToUI(
      reporting,
      values.dinerLocationRequired,
      values.dinerDepartmentRequired
    ),
    effectiveDate: transformEffectiveDateModelToUI(values.effectiveDate),
    invoiceRrule: values.invoiceRrule as CorpInvoicingPeriod,
    enableMerchantRestrictions: Boolean(
      values.defaultMerchantRestrictions?.length
    ),
    defaultMerchantRestrictions: transformPayloadToFormMerchantRestrictions(
      values.defaultMerchantRestrictions || []
    ),
    enableDinerNotificationPreferences: Boolean(
      values.dinerNotificationPreferences?.length
    ),
    dinerNotificationPreferences: transformPayloadToFormDnp(
      values.dinerNotificationPreferences || []
    ),
    firstInvoiceDate: values.firstInvoiceDate,
    enableItemRestrictions: Boolean(values.defaultItemRestrictions?.length),
    defaultItemRestrictions: transformPayloadToFormItemRestrictions(
      values.defaultItemRestrictions || []
    ),
    disableMealCreditGracePeriod: transformOrderCutoffModelToUI(
      values.orderCutOffTime
    ),
  };
}
